import { useState, useRef, useCallback } from "react";
import axios from "axios";

/**
 * useHeroNarrator — Plays Brain Superhero TTS narration for a given text.
 *
 * `play(text, hero, inCharacter, language, guided, opts)` options:
 *   - paraphraseStyle: "activity" | "celebrate" | "reflect" | null
 *     If set, fetches a list of LLM-paraphrased variations once per
 *     (hero,text,lang,style) and rotates through them on subsequent plays
 *     so the kid never hears the same script twice in a row.
 *
 * Returns: { play, stop, loading, playing, error, lastSpoken }
 */
const variationCache = new Map();
const lastIdxByKey = new Map();

const ck = (hero, text, lang, style) => `${hero}|${lang}|${style}|${text}`;

const fetchVariations = async (hero, text, lang, style, n = 3) => {
  const key = ck(hero, text, lang, style);
  if (variationCache.has(key)) return variationCache.get(key);
  try {
    const r = await axios.post("/hero/paraphrase", {
      hero, text, language: lang, style, variations: n,
    });
    const arr = (r.data?.variations || []).filter(Boolean);
    if (arr.length > 0) {
      // For celebrate style we don't cache (always fresh) but the backend
      // already enforces that — still keep it out of the cache here too.
      if (style !== "celebrate") variationCache.set(key, arr);
      return arr;
    }
  } catch {
    /* fall through to raw text */
  }
  return [text];
};

export default function useHeroNarrator() {
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState(null);
  const [lastSpoken, setLastSpoken] = useState("");
  const audioRef = useRef(null);
  const urlRef = useRef(null);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlaying(false);
  }, []);

  const play = useCallback(async (
    text,
    hero = "neutral",
    inCharacter = true,
    language = "en",
    guided = false,
    opts = {}
  ) => {
    const { paraphraseStyle = null } = opts || {};
    stop();
    setError(null);
    setLoading(true);
    try {
      // Step 1: optionally paraphrase the text so the hero sounds natural & fresh.
      let spoken = text;
      if (paraphraseStyle && hero && hero !== "neutral") {
        const variants = await fetchVariations(hero, text, language, paraphraseStyle, 3);
        if (variants && variants.length) {
          const key = ck(hero, text, language, paraphraseStyle);
          const prev = lastIdxByKey.get(key);
          let idx;
          if (paraphraseStyle === "celebrate") {
            // celebrate is freshly generated each time — single element
            idx = 0;
          } else {
            // rotate
            idx = ((prev ?? -1) + 1) % variants.length;
            lastIdxByKey.set(key, idx);
          }
          spoken = variants[idx] || text;
        }
      }
      setLastSpoken(spoken);

      // Step 2: TTS the chosen line
      const res = await axios.post(
        "/tts/narrate",
        { text: spoken, hero, in_character: inCharacter, language, guided, speed: guided ? 0.95 : 1.08 },
        { responseType: "blob", withCredentials: true }
      );
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      const url = URL.createObjectURL(res.data);
      urlRef.current = url;
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => setPlaying(false);
      audio.onerror = () => {
        setPlaying(false);
        setError("Playback failed");
      };
      await audio.play();
      setPlaying(true);
    } catch (e) {
      setError(e?.message || "TTS failed");
      setPlaying(false);
    } finally {
      setLoading(false);
    }
  }, [stop]);

  return { play, stop, loading, playing, error, lastSpoken };
}
